import { motion } from "framer-motion";
import { Sparkles, Cpu, ArrowUpRight, ShieldCheck } from "lucide-react";

const products = [
  {
    name: "TaxSetu.ai",
    tagline: "AI-powered Income Tax Research",
    description:
      "An AI research assistant trained on Indian income tax law. Ask any question, surface relevant sections, case laws, and circulars in seconds. Free for CAs & professionals.",
    metrics: [
      { v: "1800+", l: "Queries resolved" },
      { v: "Free", l: "For CAs & Pros" },
      { v: "Indian", l: "Tax law focused" },
    ],
    icon: Sparkles,
    accent: "#16D1C8",
    accent2: "#0EE6D9",
    note: "An information tool — not a substitute for professional advice.",
    badge: "AI Research",
    testid: "product-taxsetu",
  },
  {
    name: "PractiQ",
    tagline: "India's first CA Job Simulator",
    description:
      "Real-world tax and compliance scenarios in a safe environment — so your first day at work doesn't feel like your first day ever. Career-ready, before day one.",
    metrics: [
      { v: "1st", l: "of its kind" },
      { v: "Real", l: "Scenarios" },
      { v: "Safe", l: "Environment" },
    ],
    icon: Cpu,
    accent: "#FF2D7A",
    accent2: "#FF8A3D",
    note: "Built for the next generation of tax professionals.",
    badge: "Career Simulator",
    testid: "product-practiq",
  },
];

export const Products = () => {
  return (
    <section id="products" data-testid="products-section" className="relative section-pad overflow-hidden">
      <div className="shape-blob w-[500px] h-[500px] -right-40 top-20 opacity-30"
           style={{ background: "radial-gradient(circle, #FF2D7A 0%, transparent 70%)" }} />

      <div className="container-x relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="label-eyebrow mb-5">Featured · the technology layer</div>
          <h2 data-testid="products-title" className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Products that <span className="gradient-text">change the practice.</span>
          </h2>
          <p className="mt-6 text-white/65 max-w-2xl">
            Two products built from inside the profession — solving the gaps only a practicing CA would notice.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {products.map((p, i) => (
            <motion.div
              key={p.name}
              data-testid={p.testid}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group glass p-8 md:p-10 relative overflow-hidden hover:-translate-y-1 transition-all duration-500"
            >
              <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full opacity-30 group-hover:opacity-60 blur-3xl transition-opacity duration-500"
                   style={{ background: p.accent }} />
              <div className="absolute -bottom-32 -left-32 w-72 h-72 rounded-full opacity-20 group-hover:opacity-40 blur-3xl transition-opacity duration-500"
                   style={{ background: p.accent2 }} />

              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 rounded-2xl grid place-items-center border border-white/10"
                       style={{ background: `linear-gradient(135deg, ${p.accent}20, ${p.accent2}20)` }}>
                    <p.icon size={22} style={{ color: p.accent }} />
                  </div>
                  <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] border"
                        style={{ borderColor: `${p.accent}40`, color: p.accent }}>{p.badge}</span>
                </div>

                <h3 className="mt-7 font-heading font-bold text-3xl md:text-4xl text-white">
                  {p.name}
                </h3>
                <div className="mt-1.5 text-sm uppercase tracking-[0.2em]" style={{ color: p.accent }}>
                  {p.tagline}
                </div>
                <p className="mt-5 text-white/70 leading-relaxed">{p.description}</p>

                <div className="mt-7 grid grid-cols-3 gap-3">
                  {p.metrics.map((m) => (
                    <div key={m.l} className="rounded-2xl p-4 border border-white/10 bg-white/[0.02]">
                      <div className="font-heading font-bold text-xl text-white">{m.v}</div>
                      <div className="text-[11px] uppercase tracking-wider text-white/50 mt-1">{m.l}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-start gap-2 text-xs text-white/45 leading-relaxed">
                  <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                  <span>{p.note}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
